import { FC, useEffect, useState } from 'react';
import { useRoutes, useNavigate } from 'react-router-dom';
import { useSettings } from 'src/contexts/SettingsContext';
import useScrollReset from 'src/hooks/useScrollReset';
import { useAuth } from 'src/contexts/JWTContext';
import useTimeInScreen from 'src/hooks/useTimeInScreen';
import useTimeInModules from 'src/hooks/useTimeInModules';
import './config/react-pdf';
import 'src/config/i18n';
import 'src/i18n';
import useInactivityTimer from 'src/hooks/useInactivityTimer';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider } from 'material-hu';
import { MsalProvider } from '@azure/msal-react';
import { Security as OktaProvider } from '@okta/okta-react';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { PublicClientApplication } from '@azure/msal-browser';
import { OktaAuth, toRelativeUrl } from '@okta/okta-auth-js';
import { GoogleOAuthProvider } from '@react-oauth/google';
import ErrorBoundary from 'src/components/ErrorBoundary';
import {
  NewFeatures,
  TermsAndConditions,
} from 'src/components/dashboard/initialPopups';
import usePermissions from 'src/hooks/usePermissions';
import MaintenanceGuard from 'src/components/guards/MaintenanceGuard';
import SplashScreen from 'src/components/SplashScreen';
import GlobalStyles from 'src/components/GlobalStyles';
import { EventName } from 'src/types/amplitude';
import { MSAL_CONFIG } from 'src/config/azure';
import { OKTA_CONFIG } from 'src/config/okta';
import { GOOGLE_CONFIG } from 'src/config/google';
import { isRedirectError } from 'src/utils/services';
import { takeCorrectLocale } from 'src/utils/date';
import { UserPermissions } from 'src/utils/permissions';
import { setCurrentLanguage } from 'src/utils/translations';
import { createThemeConfig } from 'src/theme';
import api from 'src/config/api';
import routes from './routes';
import { initAmplitude } from './config/amplitude';

import '@stream-io/video-react-sdk/dist/css/styles.css';
import { useIconsRefetch } from './utils/icons';

const pca = new PublicClientApplication(MSAL_CONFIG);
const oktaAuth = new OktaAuth(OKTA_CONFIG);
initAmplitude();

const emptyEventProperties = {};

const { CAN_INTERACT_WITH_EXTERNAL_BOT } = UserPermissions;

const App: FC<React.PropsWithChildren<unknown>> = () => {
  const [isBotOpen, setIsBotOpen] = useState(false);
  const { settings } = useSettings();
  const { permissions, isInitialized, isAuthenticated, user, instance } =
    useAuth();
  const content = useRoutes(routes(permissions, user));
  const navigate = useNavigate();
  useScrollReset();
  const idleModal = useInactivityTimer();

  const theme = createThemeConfig(settings);

  useTimeInScreen(EventName.USER_APP_USE, emptyEventProperties);
  useTimeInModules();

  const { hasAll: canInteractWithBot } = usePermissions([
    CAN_INTERACT_WITH_EXTERNAL_BOT,
  ]);

  const correctLocale = takeCorrectLocale(user);

  useEffect(() => {
    setCurrentLanguage(user);
  }, [user]);

  useEffect(() => {
    if (
      isAuthenticated &&
      instance?.externalBotId &&
      canInteractWithBot &&
      !isBotOpen
    ) {
      const { externalBotId } = instance;
      const script = document.createElement('script');

      // @ts-ignore
      window.BOTMAKER_VAR = {
        firstName: user.firstName,
        lastName: user.lastName,
        businessName: instance.name,
        userId: user.employeeInternalId,
      };

      script.type = 'text/javascript';
      script.src = `https://go.botmaker.com/rest/webchat/p/${externalBotId}/init.js`;
      script.async = true;

      document.body.appendChild(script);
      setIsBotOpen(true);

      return () => {
        document.body.removeChild(script);
      };
    }
  }, [isAuthenticated]);

  useEffect(() => {
    api.interceptors.response.use(
      response => response,
      error => {
        if (isRedirectError(error)) {
          navigate('/');
          return;
        }
        return Promise.reject(error);
      },
    );
  }, [navigate]);

  const restoreOriginalUri = (_oktaAuth, originalUri) => {
    navigate(toRelativeUrl(originalUri || '/', window.location.origin), {
      replace: true,
    });
  };
  const iconsFetched = useIconsRefetch();

  return (
    <LocalizationProvider
      dateAdapter={AdapterDateFns}
      adapterLocale={correctLocale}
    >
      <OktaProvider
        oktaAuth={oktaAuth}
        restoreOriginalUri={restoreOriginalUri}
      >
        <MsalProvider instance={pca}>
          <GoogleOAuthProvider clientId={GOOGLE_CONFIG.clientId}>
            <ThemeProvider theme={theme}>
              <ErrorBoundary>
                <SnackbarProvider
                  dense
                  maxSnack={3}
                >
                  <GlobalStyles />
                  <MaintenanceGuard>
                    {isInitialized && iconsFetched ? content : <SplashScreen />}
                    {isAuthenticated && <TermsAndConditions />}
                    {isAuthenticated && <NewFeatures />}
                    {idleModal}
                  </MaintenanceGuard>
                </SnackbarProvider>
              </ErrorBoundary>
            </ThemeProvider>
          </GoogleOAuthProvider>
        </MsalProvider>
      </OktaProvider>
    </LocalizationProvider>
  );
};

export default App;
